import { Stack, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { keyBy } from 'lodash';
import React, { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { AccordionProps } from '@/components/Accordion/Accordion';
import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { useFormContext } from '@/components/react-hook-form';
import {
  CombinedFormShape,
  CombinedPaths,
} from '@/modules/dispositiveProvisions/dispositiveProvisions.types';
import { DispositiveProvisionTileIcon } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/DispositiveProvisionTileIcon';
import {
  DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE,
  PrimaryClientDeathEvent,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { useSemanticTileKindNode } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/hooks/useSemanticTileKindNode';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';
import {
  ClientOrganizationKind,
  DispositiveProvisionDispositionKind,
  EntityInEstateStatus,
  EntityKind,
  TestamentaryEntityKind,
  TransferMethod,
} from '@/types/schema';
import { formatPercentSmart } from '@/utils/formatting/percent';

import { useDispositiveProvisionsContext } from '../../contexts/dispositiveProvisions.context';
import {
  formatAssetDispositionLabel,
  formatDispositionKindCopy,
  hasOnlyInsurancePolicies,
} from '../../dispositiveProvisions.formatting';
import {
  DISPOSITIVE_PROVISIONS_FORM_NAMESPACE,
  getTransferTaxKindOptions,
} from '../DispositiveProvisionsForm.constants';
import { DispositiveProvisionsFormRecipient } from '../DispositiveProvisionsForm.types';
import {
  buildCompositeAssetValues,
  getAssetOptionProseLabel,
  useEntityAssetOptions,
} from '../hooks/useEntityAssetOptions';
import { useHouseholdAssetsForCrossEntityDp } from '../hooks/useHouseholdAssetsForCrossEntityDp';
import {
  SURVIVING_SPOUSE_SENTINEL,
  useRecipientOptions,
  useRecipientOptionsParams,
} from '../hooks/useRecipientOptions';
import { DispositiveProvisionsRecipientTemplateRecipientConfiguration } from './DispositiveProvisionsRecipient.types';
import { RecipientNotesBadge } from './RecipientNotesBadge';

interface RecipientIdMetadata {
  displayName: string;
  isCharitableOrganization?: boolean;
  entityKind?: EntityKind;
  inEstateStatus?: EntityInEstateStatus;
  testamentaryEntityKind?: TestamentaryEntityKind;
  kind: 'entity' | 'testamentaryEntity' | 'clientProfile' | 'organization';
}

export function useRecipientIdMetadataMap(): Record<
  string,
  RecipientIdMetadata
> {
  const { primaryClients } = useHouseholdDetailsContext();
  const recipientOptionsParams = useRecipientOptionsParams();
  const {
    options: recipientOptions,
    organizationRecipients,
    entityRecipients,
    testamentaryEntityRecipients,
    clientProfileRecipients,
  } = useRecipientOptions(recipientOptionsParams);
  return useMemo(() => {
    const orgMap = keyBy(organizationRecipients, 'id');
    const entityMap = keyBy(entityRecipients, 'id');
    const testamentaryEntityMap = keyBy(testamentaryEntityRecipients, 'id');
    const clientProfileMap = keyBy(clientProfileRecipients, 'id');

    return recipientOptions.reduce(
      (acc, recipientOption) => {
        if ('component' in recipientOption) return acc;

        const isCharitableOrganization =
          orgMap[recipientOption.value]?.kind ===
          ClientOrganizationKind.CharitableOrganization;
        const entityKind = entityMap[recipientOption.value]?.kind;
        const inEstateStatus = (() => {
          const entity = entityMap[recipientOption.value];
          if (
            entity &&
            'subtype' in entity &&
            'inEstateStatus' in entity.subtype
          ) {
            return entity.subtype.inEstateStatus ?? undefined;
          }

          const testamentaryEntity =
            testamentaryEntityMap[recipientOption.value];
          if (testamentaryEntity && 'inEstateStatus' in testamentaryEntity) {
            return testamentaryEntity.inEstateStatus ?? undefined;
          }

          const clientProfile = clientProfileMap[recipientOption.value];

          if (clientProfile) {
            if (primaryClients?.find((c) => c.id === clientProfile.id)) {
              return EntityInEstateStatus.InEstate;
            }

            return EntityInEstateStatus.OutOfEstate;
          }

          return undefined;
        })();

        const kind = (() => {
          if (recipientOption.value === SURVIVING_SPOUSE_SENTINEL) {
            return 'clientProfile';
          }

          if (entityMap[recipientOption.value]) {
            return 'entity';
          }
          if (testamentaryEntityMap[recipientOption.value]) {
            return 'testamentaryEntity';
          }
          if (clientProfileMap[recipientOption.value]) {
            return 'clientProfile';
          }
          return 'organization';
        })();

        return {
          ...acc,
          [recipientOption.value]: {
            displayName: recipientOption.display,
            isCharitableOrganization,
            entityKind,
            inEstateStatus,
            testamentaryEntityKind:
              testamentaryEntityMap[recipientOption.value]?.kind,
            kind,
          },
        };
      },
      {} as Record<string, RecipientIdMetadata>
    );
  }, [
    organizationRecipients,
    entityRecipients,
    testamentaryEntityRecipients,
    clientProfileRecipients,
    recipientOptions,
    primaryClients,
  ]);
}

export function useRecipientAccordionProps(
  recipientIndex: number,
  hasStateTax: boolean,
  recipientConfiguration: DispositiveProvisionsRecipientTemplateRecipientConfiguration,
  isOptional = false,
  Draghandle: React.ReactNode,
  deathEvent?: PrimaryClientDeathEvent
): Pick<
  AccordionProps,
  'title' | 'subtitle' | 'description' | 'leftAdornment' | 'rightAdornment'
> {
  const { control } = useFormContext<CombinedFormShape>();
  const recipientIdToMetadata = useRecipientIdMetadataMap();
  const { id: entityId, householdId } = useDispositiveProvisionsContext();

  type RecipientsBasePath =
    | `${typeof DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients`
    | `${typeof DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE}.firstDeath.recipients`
    | `${typeof DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE}.secondDeath.recipients`;

  const recipientsBasePath: RecipientsBasePath =
    deathEvent !== undefined
      ? (`${DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE}.${
          deathEvent === PrimaryClientDeathEvent.FIRST_DEATH
            ? 'firstDeath'
            : 'secondDeath'
        }.recipients` as const)
      : (`${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients` as const);

  function getRecipientNameFromId(recipientId: string | null) {
    if (!recipientId) return 'Recipient';
    const possibleDisplayName = recipientIdToMetadata[recipientId]?.displayName;
    return possibleDisplayName || 'Recipient';
  }

  const transferTaxKind = useWatch({
    control,
    name: `${recipientsBasePath}.${recipientIndex}.transferTaxKind` as const satisfies CombinedPaths,
  });

  const recipientId = useWatch({
    control,
    name: `${recipientsBasePath}.${recipientIndex}.recipientId` as const satisfies CombinedPaths,
  });

  const recipient = useWatch({
    control,
    name: `${recipientsBasePath}.${recipientIndex}` as const satisfies CombinedPaths,
  });

  const isAsset = recipient?.dispositionKind_ButtonGroup === 'asset';

  const { options: entityAssetOptions } = useEntityAssetOptions(
    isAsset ? entityId : null
  );
  // The cross-entity drawer can populate an asset DP with assets that live on
  // other household entities. Pull the same household-wide pool so the card
  // header can resolve display names for those picks instead of falling back
  // to a bare percentage like "100%". Skip when this isn't an asset DP — the
  // result wouldn't be consumed.
  const { allOptions: householdAssetOptions } =
    useHouseholdAssetsForCrossEntityDp({
      householdId,
      excludeEntityId: entityId,
      skip: !isAsset,
    });
  const assetOptions = useMemo(
    () => [...entityAssetOptions, ...householdAssetOptions],
    [entityAssetOptions, householdAssetOptions]
  );

  const semanticTileKindNode = useSemanticTileKindNode({
    recipientKind: recipient?.recipientKind,
    recipientId: recipient?.recipientId ?? '',
  });

  if (!recipient) {
    return {
      title: 'Recipient',
    };
  }

  const transferTaxKindDisplay =
    getTransferTaxKindOptions({
      hasStateTax,
      recipientConfiguration,
    }).find((o) => o.value === transferTaxKind)?.display ?? undefined;

  const sharedLeftAdornment = (
    <Stack direction="row" alignItems="center" spacing={1} paddingRight={1}>
      {Draghandle}
      <DispositiveProvisionTileIcon
        size="large"
        variant="subtle"
        node={semanticTileKindNode}
      />
    </Stack>
  );

  const distributionValue = isAsset
    ? getAssetDistributionValue(recipient, assetOptions)
    : getDistributionSummary(recipient);

  const displayValue = distributionValue || '';

  return {
    title: (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="title-xs" color={COLORS.TEXT.PRIMARY}>
          {getRecipientNameFromId(recipientId)}
        </Typography>
        {recipient.notes && <RecipientNotesBadge notes={recipient.notes} />}
      </Stack>
    ),
    subtitle: transferTaxKindDisplay,
    leftAdornment: sharedLeftAdornment,
    rightAdornment: (
      <Stack spacing={0.5} alignItems="flex-end" minWidth={0}>
        {displayValue && (
          <Typography
            variant="body-xs"
            noWrap
            title={displayValue}
            sx={{ maxWidth: '100%' }}
          >
            {displayValue}
          </Typography>
        )}
        {isOptional && (
          <Badge variant={BadgeVariants.Accent} display="Optional" />
        )}
      </Stack>
    ),
  };
}

export function getDistributionSummary(
  recipient: DispositiveProvisionsFormRecipient
): string {
  const { dispositionKind_ButtonGroup, dispositionKind_Select } = recipient;

  // if no kind is selected, there's nothing we can do
  if (!dispositionKind_ButtonGroup && !dispositionKind_Select) return '';

  // Resolve the split form fields into a single dispositionKind
  const dispositionKind = (() => {
    if (dispositionKind_ButtonGroup === 'other') {
      if (!dispositionKind_Select) return null;
      return dispositionKind_Select;
    }
    return dispositionKind_ButtonGroup;
  })();

  if (!dispositionKind) return '';

  // Asset DPs are handled by getAssetDistributionDescription which has
  // access to asset options for display names.
  if (dispositionKind === 'asset') {
    return '';
  }

  // Early return for empty required values
  if (
    dispositionKind === DispositiveProvisionDispositionKind.Percentage &&
    !recipient.dispositionPercentage
  ) {
    return '';
  }
  if (
    dispositionKind === DispositiveProvisionDispositionKind.Amount &&
    !recipient.dispositionAmount
  ) {
    return '';
  }

  return formatDispositionKindCopy({
    dispositionKind,
    dispositionPercentage: recipient.dispositionPercentage ?? null,
    dispositionFormulaicPercentage:
      recipient.dispositionFormulaicPercentage ?? null,
    dispositionAmount: recipient.dispositionAmount ?? null,
    dispositionLimit: recipient.dispositionLimit ?? null,
    growByInflation: recipient.growByInflation ?? false,
  });
}

/**
 * Returns the distribution value of an asset DP including the transfer method,
 * e.g. "100% of Life insurance ($500,000 death benefit)" or
 * "100% of Property A, in kind".
 */
function getAssetDistributionValue(
  recipient: DispositiveProvisionsFormRecipient,
  assetOptions: TypeaheadSelectInputOption<string>[]
): string {
  // Build composite values to look up display names from the options
  const compositeValues = buildCompositeAssetValues(recipient);

  if (compositeValues.length === 0) {
    return '';
  }

  const optionsByValue = keyBy(assetOptions, 'value');
  // Use the prose label so insurance policies retain their death-benefit
  // context (e.g. "Lincoln National ($1M death benefit)") that `display`
  // alone no longer carries — see `getAssetOptionProseLabel`.
  const assetNames = compositeValues
    .map((v) => {
      const option = optionsByValue[v];
      return option ? getAssetOptionProseLabel(option) : undefined;
    })
    .filter((v): v is string => Boolean(v));

  const pct = recipient.dispositionAssetPercentage ?? new Decimal(100);

  if (assetNames.length === 0) {
    return `${formatPercentSmart(pct)}%`;
  }

  const isInsuranceOnly = hasOnlyInsurancePolicies({
    insurancePolicies: recipient.insurancePolicyIds?.length ?? 0,
    assetGroups: recipient.assetGroupIds?.length ?? 0,
    assetStatics: recipient.assetStaticIds?.length ?? 0,
    ownershipStakes: recipient.ownershipStakeIds?.length ?? 0,
  });

  return formatAssetDispositionLabel({
    pct,
    assetNames,
    // Default to InKind when not set — matches prior inline behavior where
    // the else branch produced 'in kind'.
    transferMethod: recipient.transferMethod ?? TransferMethod.InKind,
    isInsuranceOnly,
  });
}
